import { useTheme } from '@mui/material/styles';
import { Chip, IconButton, TableCell, TableRow } from '@mui/material';
import React, {
  CSSProperties,
  memo,
  MouseEventHandler,
  useCallback,
  useMemo,
} from 'react';
import { BankAccountOperation } from '../../types/bank-account/BankAccountOperation';
import { CheckBox, CheckBoxOutlineBlank, Help } from '@mui/icons-material';
import { BankAccountOperationColumn } from '../../types/bank-account/BankAccountOperationColumn';
import { DraggableProvided } from '@hello-pangea/dnd';
import Dinero from 'dinero.js';
import Color from 'color';
import { GripIcon, SelectedGripIcon } from '../../assets/GripIcon';
import { AccountOperationCellWithTooltip } from './AccountOperationCellWithTooltip';
import { CATEGORIES_PER_KEYS } from '../../types/Categories';

export interface AccountOperationRowProps {
  item: BankAccountOperation;
  onColumnClick?: (column: BankAccountOperationColumn, rect: DOMRect) => void;
  onColumnDoubleClick?: (
    column: BankAccountOperationColumn,
    rect: DOMRect,
  ) => void;
  selected?: boolean;
  isDragging?: boolean;

  style?: CSSProperties;

  draggableProvided?: DraggableProvided;

  onMouseEnter?: (el: HTMLElement) => void;
  onMouseLeave?: () => void;

  onContextMenu?: (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;

  onCategoryButtonClick?: () => void;
}

export const AccountOperationRow = memo((props: AccountOperationRowProps) => {
  const {
    item,
    style,
    onColumnClick,
    onColumnDoubleClick,
    selected,
    isDragging,
    draggableProvided,
    onContextMenu,
    onCategoryButtonClick,
  } = props;

  const theme = useTheme();

  const hoverBg = useMemo(() => {
    const color = Color(theme.palette.background.paper);
    return color.isDark()
      ? color.lighten(0.05).hex()
      : color.darken(0.05).hex();
  }, [theme.palette.background.paper]);

  const itemColor = item.color || '#ffffff';

  const tableRowSx = useMemo(
    () => ({
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'stretch',
      backgroundColor: selected ? hoverBg : 'background.paper',
      boxShadow: isDragging ? 4 : 0,
      ...(selected && { transition: 'background-color 200ms ease-in-out' }),
    }),
    [isDragging, selected, hoverBg],
  );

  const tableCellSx = useMemo(
    () => ({
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      flexBasis: 0,
      transition: 'background-color 200ms ease-in-out',
      '&:hover': {
        backgroundColor: hoverBg,
        cursor: 'pointer',
      },
    }),
    [hoverBg],
  );

  const columnClickHandler = useCallback(
    (column: BankAccountOperationColumn): MouseEventHandler<HTMLDivElement> =>
      (e) => {
        if (onColumnClick) {
          onColumnClick(column, e.currentTarget.getBoundingClientRect());
        }
      },
    [onColumnClick],
  );

  const columnDblClickHandler = useCallback(
    (column: BankAccountOperationColumn): MouseEventHandler<HTMLDivElement> =>
      (e) => {
        if (onColumnDoubleClick) {
          onColumnDoubleClick(column, e.currentTarget.getBoundingClientRect());
        }
      },
    [onColumnDoubleClick],
  );

  const itemAmount = useMemo(() => Dinero(item.amount), [item.amount]);
  const itemTotal = useMemo(() => Dinero(item.total), [item.total]);

  const isColored =
    item.color != null && item.color.toLowerCase() !== '#ffffff';
  const category =
    item.category != null ? CATEGORIES_PER_KEYS[item.category] : null;

  return (
    <TableRow
      ref={draggableProvided?.innerRef}
      component={'div'}
      sx={tableRowSx}
      {...draggableProvided?.draggableProps}
      style={style}
      onMouseEnter={(event: any) => {
        if (props.onMouseEnter) {
          props.onMouseEnter(event.currentTarget);
        }
      }}
      onMouseLeave={props.onMouseLeave}
      onContextMenu={onContextMenu}
    >
      <TableCell
        component={'div'}
        sx={{
          ...tableCellSx,
          padding: 0,
          flex: '0 0 auto',
          textAlign: 'center',
        }}
        {...draggableProvided?.dragHandleProps}
        onClick={columnClickHandler(BankAccountOperationColumn.DND)}
        onDoubleClick={columnDblClickHandler(BankAccountOperationColumn.DND)}
      >
        {selected ? <SelectedGripIcon /> : <GripIcon />}
      </TableCell>
      <TableCell
        component={'div'}
        sx={tableCellSx}
        style={{ flex: 0, flexBasis: 40, textAlign: 'center' }}
        onClick={columnClickHandler(BankAccountOperationColumn.CHECKED)}
        onDoubleClick={columnDblClickHandler(
          BankAccountOperationColumn.CHECKED,
        )}
      >
        {item.checked ? <CheckBox /> : <CheckBoxOutlineBlank />}
      </TableCell>
      <TableCell
        component={'div'}
        sx={tableCellSx}
        style={{ flex: 1 }}
        onClick={columnClickHandler(BankAccountOperationColumn.DATE)}
        onDoubleClick={columnDblClickHandler(BankAccountOperationColumn.DATE)}
      >
        {item.date}
      </TableCell>
      <AccountOperationCellWithTooltip
        value={item.type}
        sx={tableCellSx}
        style={{ flex: 1 }}
        onClick={columnClickHandler(BankAccountOperationColumn.TYPE)}
        onDoubleClick={columnDblClickHandler(BankAccountOperationColumn.TYPE)}
      />
      <TableCell
        component={'div'}
        sx={{
          ...tableCellSx,
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          ...(isColored && { paddingTop: 0, paddingBottom: 0 }),
        }}
        style={{ flex: 5 }}
        onClick={columnClickHandler(BankAccountOperationColumn.DESCRIPTION)}
        onDoubleClick={columnDblClickHandler(
          BankAccountOperationColumn.DESCRIPTION,
        )}
      >
        {isColored ? (
          <Chip
            sx={{
              cursor: 'pointer',
              fontSize: '0.875rem',
              backgroundColor: itemColor,
              color: theme.palette.getContrastText(itemColor),
            }}
            label={item.description}
          />
        ) : (
          item.description
        )}
        <div style={{ flex: 1 }} />
        <IconButton
          disabled={onCategoryButtonClick == null}
          onClick={onCategoryButtonClick}
          size={'small'}
        >
          {category ? (
            <category.icon htmlColor={category.color} />
          ) : (
            <Help htmlColor={theme.palette.grey[500]} />
          )}
        </IconButton>
      </TableCell>
      <TableCell
        component={'div'}
        sx={{ ...tableCellSx, color: 'success.main', fontWeight: 'bold' }}
        style={{ flex: 1 }}
        onClick={columnClickHandler(BankAccountOperationColumn.AMOUNT_CREDIT)}
        onDoubleClick={columnDblClickHandler(
          BankAccountOperationColumn.AMOUNT_CREDIT,
        )}
      >
        {itemAmount.isPositive() && !itemAmount.isZero()
          ? itemAmount.toFormat()
          : ''}
      </TableCell>
      <TableCell
        component={'div'}
        sx={{ ...tableCellSx, color: 'error.main' }}
        style={{ flex: 1 }}
        onClick={columnClickHandler(BankAccountOperationColumn.AMOUNT_DEBIT)}
        onDoubleClick={columnDblClickHandler(
          BankAccountOperationColumn.AMOUNT_DEBIT,
        )}
      >
        {itemAmount.isNegative() ? itemAmount.multiply(-1).toFormat() : ''}
      </TableCell>
      <TableCell
        component={'div'}
        sx={tableCellSx}
        style={{ flex: 1 }}
        onClick={columnClickHandler(BankAccountOperationColumn.TOTAL)}
        onDoubleClick={columnDblClickHandler(BankAccountOperationColumn.TOTAL)}
      >
        {item.total ? itemTotal.toFormat() : ''}
      </TableCell>
    </TableRow>
  );
});
AccountOperationRow.displayName = 'AccountOperationRow';
